import {
	useInfiniteQuery,
	useQuery,
	type UseQueryOptions,
	type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import api from 'modules/queries/api';
import { type ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import {
	LWApiPublicAssetListParamsRawI,
	LWApiPublicAssetListResultRawI,
} from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import { supplementDetails, type AssetDetails } from './useDetails';

export const ASSET_LIST_QUERY_KEY = '/asset/list';

const useAssetList = (
	params: LWApiPublicAssetListParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicAssetListResultRawI,
		ApiErrorI,
		Omit<LWApiPublicAssetListResultRawI, 'items'> & { items: AssetDetails[] }
	>,
) =>
	useQuery({
		queryKey: [ASSET_LIST_QUERY_KEY, params],
		queryFn: () => api.asset.apiRaw.list(params),
		...options,
		select: (data) => ({
			...data,
			items: data.items.map(supplementDetails),
		}),
	});

export const useInfiniteAssetList = (
	params: Omit<LWApiPublicAssetListParamsRawI, 'page_num'>,
	options?: UseInfiniteQueryOptions<
		LWApiPublicAssetListResultRawI,
		ApiErrorI,
		Omit<LWApiPublicAssetListResultRawI, 'items'> & { items: AssetDetails[] }
	>,
) =>
	useInfiniteQuery({
		queryKey: [ASSET_LIST_QUERY_KEY, params],
		queryFn: ({ pageParam }) =>
			api.asset.apiRaw.list({ ...params, page_num: pageParam || 1 }),
		getNextPageParam: (lastPage) => {
			const { page_num: lastPageNum, page_total: pageTotal } = lastPage;

			if (lastPageNum === pageTotal) return undefined;
			return lastPageNum + 1;
		},
		select: (data) => ({
			...data,
			pages: data.pages.map((page) => ({
				...page,
				items: page.items.map(supplementDetails),
			})),
		}),
		...options,
	});

export default useAssetList;
